import { _ } from "../../../../core/polyglot/polyglot.js";
import { select } from "../../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import DateRangePickerWidget from "../../../../core/widgets/DateRangePickerWidget/DateRangePickerWidget.js";
import ledgerSearchFieldWidget from "../../ledgerSearchFieldWidget.js";
import ToggableWidget from "../../../../core/widgets/ToggableWidget/ToggableWidget.js";

/**
 * Renders a form to let the user specify what to search for. The form
 * contains a text field by default and an "Advanced search" button to
 * show extra search options. These options include a dropdown to
 * filter the area to search for (i.e., accounts, transactions or
 * ledger entries) and a date range picker.
 *
 * @param {function} onSearchRequest - Takes a search query as
 *   argument and is expected to trigger a search. The search query
 *   consists of a searchPeriod, a searchText and a searchArea
 */
export default class SearchFormWidget extends Widget {
	constructor({ onSearchRequest, searchPeriod }) {
		super(...arguments);

		this._onSearchRequest = onSearchRequest;

		this._ledgerSearchField = this._makeLedgerSearchField();
		this._searchAreaDropdown = this._makeSearchAreaDropdown();
		this._dateRangeWidget = this._makeDateRangeWidget({ searchPeriod });

		this._advancedSearchToggableWidget = new ToggableWidget({
			visible: false,
			content: this._renderAdvancedSearchOn.bind(this),
		});
	}

	renderContentOn(html) {
		html.div(
			{ class: "row" },
			html.div(
				{ class: "col-md-8 col-md-offset-2", "data-help": "search" },
				this._ledgerSearchField,
			),
		);

		html.div(
			{ class: "row" },
			html.div(
				{ class: "col-md-8 col-md-offset-2" },
				html.a(
					{
						class: "pull-right advanced-search-anchor",
						"data-test": "advanced-search",
						click: () =>
							this._advancedSearchToggableWidget.toggle(),
					},
					_("Advanced search"),
				),
			),
		);

		html.div(
			{ class: "row" },
			html.div(
				{ class: "col-md-8 col-md-offset-2" },
				this._advancedSearchToggableWidget,
			),
		);
	}

	_renderAdvancedSearchOn(html) {
		html.div(
			{ class: "advanced-search" },
			html.div(
				{ class: "search-section row" },
				html.div(
					{ klass: "col-lg-2 col-sm-3" },
					html.h5(_("Search for")),
				),
				html.div(
					{ klass: "col-lg-10 col-sm-9 search-area" },
					this._searchAreaDropdown,
				),
			),
			html.div(
				{ class: "search-section row" },
				html.div(
					{ klass: "col-lg-2 col-sm-3" },
					html.h5(_("Date range")),
				),
				html.div(
					{ klass: "col-lg-10 col-sm-9" },
					this._dateRangeWidget,
				),
			),
		);
	}

	_makeSearchAreaDropdown() {
		const items = [
			{ label: _("Everything"), value: "All" },
			{ label: _("Accounts"), value: "Accounts" },
			{ label: _("Transactions"), value: "Transactions" },
			{ label: _("Ledger entries"), value: "LedgerEntries" },
		];

		this._selectedSearchArea = items[0];

		return select({
			ref: (ref) => ref.setAttribute("name", "scope"),
			items,
			selected: this._selectedSearchArea,
			onChange: (value) => {
				this._selectedSearchArea = value;
				this._search();
			},
		});
	}

	_makeLedgerSearchField() {
		const ledgerSearchField = ledgerSearchFieldWidget();

		ledgerSearchField.onSearch.register(() => this._search());

		return ledgerSearchField;
	}

	_makeDateRangeWidget({ searchPeriod }) {
		const dateRangeWidget = new DateRangePickerWidget({
			startDate: searchPeriod.getStart(),
			endDate: searchPeriod.getEnd(),
		});

		dateRangeWidget.onDateRangeChanged.register(() => this._search());

		return dateRangeWidget;
	}

	_search() {
		const searchPeriod = this._dateRangeWidget.asTimespan();
		const searchText = this._ledgerSearchField.getSearchString();
		const searchArea = this._selectedSearchArea;

		if (!searchText) {
			return;
		}

		this._onSearchRequest({ searchPeriod, searchText, searchArea });
	}
}
